/**
 * The browser and operating system in a stored user-agent string, for the
 * Active sessions list (D1045: it showed the raw string). A small local
 * reader, not a library: it names the browsers and systems people sign in
 * with, and answers null for anything else, so the screen says "Unknown
 * device" rather than guess. The raw string stays available beside it (the
 * screen's tooltip), because a label is a reading of the data, not the data.
 *
 * Order matters: Edge, Opera and Samsung Internet also carry "Chrome/", and
 * every Chromium and WebKit browser carries "Safari/", so the specific
 * tokens are tried first. iOS says "like Mac OS X" and Android says
 * "Linux", so those systems are tried before macOS and Linux.
 *
 * The names are product names, written the same in every language; the
 * sentence around them ("Chrome on Windows") is the screen's, translated.
 */
export interface DeviceDescription {
  browser: string | null;
  os: string | null;
}

const BROWSERS: ReadonlyArray<[RegExp, string]> = [
  [/\bEdg(?:e|A|iOS)?\//, "Edge"],
  [/\bOPR\/|\bOpera\b/, "Opera"],
  [/\bSamsungBrowser\//, "Samsung Internet"],
  [/\bFirefox\/|\bFxiOS\//, "Firefox"],
  [/\bChrome\/|\bCriOS\//, "Chrome"],
  [/\bVersion\/[\d.]+.*\bSafari\//, "Safari"],
  [/\bMSIE |\bTrident\//, "Internet Explorer"],
];

const SYSTEMS: ReadonlyArray<[RegExp, string]> = [
  [/\bWindows\b/, "Windows"],
  [/\b(?:iPhone|iPad|iPod)\b/, "iOS"],
  [/\bAndroid\b/, "Android"],
  [/\bCrOS\b/, "ChromeOS"],
  [/\bMac OS X\b|\bMacintosh\b/, "macOS"],
  [/\bLinux\b/, "Linux"],
];

const first = (
  table: ReadonlyArray<[RegExp, string]>,
  ua: string,
): string | null => table.find(([pattern]) => pattern.test(ua))?.[1] ?? null;

/** What the string says, or null when it names neither a browser nor a system. */
export function describeUserAgent(ua: string): DeviceDescription | null {
  if (typeof ua !== "string" || ua.trim() === "") return null;
  const browser = first(BROWSERS, ua);
  const os = first(SYSTEMS, ua);
  return browser === null && os === null ? null : { browser, os };
}
